import { requisicaoAutenticada } from './http';

/**
 * Papel de **negócio** da empresa no ICMS — `GET /api/icms/perfil/:businessId`.
 *
 * ## O que isto resolve
 *
 * Há três dashboards (cliente, assessor, fornecedor) e quem escolhe entre eles
 * é o que a **empresa** é na operação, não o que a **pessoa** pode fazer. O
 * token carrega `PapelUsuario` (`ADMIN`, `STAFF`, `BUSINESS_OWNER`,
 * `BUSINESS_EMPLOYER`) e não diz — nem deveria dizer — o `IcmsType` do negócio.
 * Confundir os dois foi a dívida #41: "CLIENT"/"ADVISOR"/"SUPPLIER" apareciam em
 * menu e Swagger do legado sem existir em enum ou guard nenhum.
 *
 * Sem esta rota a tela teria que chutar, e chutar o dashboard de cliente devolve
 * 403 para assessor e fornecedor — ou, pior, uma tela vazia que se lê como "não
 * tenho operação".
 *
 * Espelha `PerfilOperacional` de `packages/domain/src/icms/perfil-operacional.ts`
 * (o web não importa o domínio — `docs/arquitetura.md`).
 */

/** Espelha `PapelIcms` do domínio. */
export type PapelIcms = 'CLIENT' | 'ADVISOR' | 'SUPPLIER';

/** Qual dashboard atende cada papel. */
export type DashboardIcms = 'CLIENTE' | 'ASSESSOR' | 'FORNECEDOR';

export interface PerfilOperacional {
  readonly businessId: string;
  /** Papéis ICMS ativos, em ordem estável (a ordem é do backend). */
  readonly papeis: readonly PapelIcms[];
  /**
   * Dashboards que fazem sentido para a empresa. Mais de um quando ela acumula
   * papéis — §2.1 prevê a mesma empresa cliente numa operação e fornecedora em
   * outra.
   */
  readonly dashboards: readonly DashboardIcms[];
  /**
   * O dashboard a abrir por padrão. **`null` não é erro**: é empresa cadastrada
   * que ainda não entrou em operação de ICMS.
   */
  readonly dashboardPadrao: DashboardIcms | null;
}

export async function carregarPerfilOperacional(
  businessId: string,
  sinal?: AbortSignal,
): Promise<PerfilOperacional> {
  return requisicaoAutenticada<PerfilOperacional>(
    `/icms/perfil/${encodeURIComponent(businessId)}`,
    { sinal },
  );
}
